
import * as React from 'react';
import { action, observable } from 'mobx';
import {
	Model,
	IModelAttributes,
	attribute,
	entity,
	ReferencePath,
} from 'Models/Model';
import * as Models from 'Models/Entities';
import { CRUD } from '../CRUDOptions';
import * as AttrUtils from 'Util/AttributeUtils';
import { IAcl } from 'Models/Security/IAcl';
import {
	makeFetchOneToManyFunc,
	getCreatedModifiedCrudOptions,
} from 'Util/EntityUtils';
import VisitorsLogo from 'Models/Security/Acl/VisitorsLogo';
import UserLogo from 'Models/Security/Acl/UserLogo';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import { FileListPreview } from 'Views/Components/CRUD/Attributes/AttributeFile';
import SuperAdministratorScheme from '../Security/Acl/SuperAdministratorScheme';


export interface ILogoEntityAttributes extends IModelAttributes {
	fileNameId: string;
	fileName: Blob;

	esigntemplatess: Array<
		| Models.EsigntemplateEntity
		| Models.IEsigntemplateEntityAttributes
	>;
	businessEntityId?: string;
	businessEntity?: Models.BusinessEntity | Models.IBusinessEntityAttributes;

}

@entity('LogoEntity', 'Logo')
export default class LogoEntity extends Model
	implements ILogoEntityAttributes {
	public static acls: IAcl[] = [
		new SuperAdministratorScheme(),
		new VisitorsLogo(),
		new UserLogo(),

	];

	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	@observable
	@attribute<LogoEntity, string>({ file: 'fileName' })
	@CRUD({
		name: 'File Name',
		displayType: 'file',
		order: 10,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseUuid,
		fileAttribute: 'fileName',
		displayFunction: attr => attr
			? <FileListPreview url={attr} />
			: 'No File Attached',
	})
	public fileNameId: string;

	@observable
	public fileName: Blob;

	@observable
	@attribute({ isReference: true, manyReference: true })
	@CRUD({
		
		name: 'ESignTemplatess',
		displayType: 'reference-multicombobox',
		order: 20,
		referenceTypeFunc: () => Models.EsigntemplateEntity,
		referenceResolveFunction: makeFetchOneToManyFunc({
			relationName: 'esigntemplatess',
			oppositeEntity: () => Models.EsigntemplateEntity,
		}),
		
	})
	public esigntemplatess: Models.EsigntemplateEntity[] = [];

	@observable
	@attribute()
	@CRUD({
		
		name: 'Business Entity',
		displayType: 'reference-combobox',
		order: 30,
		referenceTypeFunc: () => Models.BusinessEntity,
		
	})
	public businessEntityId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public businessEntity: Models.BusinessEntity;



	// eslint-disable-next-line @typescript-eslint/no-useless-constructor
	constructor(attributes?: Partial<ILogoEntityAttributes>) {
		super(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	@action
	public assignAttributes(attributes?: Partial<ILogoEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.fileName !== undefined) {
				this.fileName = attributes.fileName;
			}
			if (attributes.fileNameId !== undefined) {
				this.fileNameId = attributes.fileNameId;
			}
			if (attributes.esigntemplatess !== undefined && Array.isArray(attributes.esigntemplatess)) {
				for (const model of attributes.esigntemplatess) {
					if (model instanceof Models.EsigntemplateEntity) {
						this.esigntemplatess.push(model);
					} else {
						this.esigntemplatess.push(new Models.EsigntemplateEntity(model));
					}
				}
			}
			if (attributes.businessEntityId !== undefined) {
				this.businessEntityId = attributes.businessEntityId;
			}
			if (attributes.businessEntity !== undefined) {
				if (attributes.businessEntity === null) {
					this.businessEntity = attributes.businessEntity;
				} else if (attributes.businessEntity instanceof Models.BusinessEntity) {
					this.businessEntity = attributes.businessEntity;
					this.businessEntityId = attributes.businessEntity.id;
				} else {
					this.businessEntity = new Models.BusinessEntity(attributes.businessEntity);
					this.businessEntityId = this.businessEntity.id;
				}
			}

		}
	}

	/**
	 * Additional fields that are added to GraphQL queries when using the
	 * the managed model APIs.
	 */
	public defaultExpands = `
		esigntemplatess {
			${Models.EsigntemplateEntity.getAttributes().join('\n')}
		}
		businessEntity {
			${Models.BusinessEntity.getAttributes().join('\n')}
		}
	`;

	/**
	 * The save method that is called from the admin CRUD components.
	 */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public async saveFromCrud(formMode: EntityFormMode) {
		const relationPath: ReferencePath = {
			esigntemplatess: {},
		};
		return this.save(
			relationPath,
			{
				options: [
					{
						key: 'mergeReferences',
						graphQlType: '[String]',
						value: [
							'esigntemplatess',
						],
					},
				],
				contentType: 'multipart/form-data',
			},
		);
	}

	/**
	 * Returns the string representation of this entity to display on the UI.
	 */
	public getDisplayName() {
		return this.id;
	}
}

/*
 * Retrieve the created and modified CRUD attributes for defining the CRUD views and decorate the class with them.
 */
const [createdAttr, modifiedAttr] = getCreatedModifiedCrudOptions();
CRUD(createdAttr)(LogoEntity.prototype, 'created');
CRUD(modifiedAttr)(LogoEntity.prototype, 'modified');
